/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { createContext } from 'react';

const blinkTime = 200; // ms
const MAX_LOG_ENTRIES = 100;

export interface AsyncOpEvent {
    id: string;
    label: string;
    stream: 'cmd' | 'stdout' | 'stderr' | 'progress' | 'result';
    text: string;
    // Progress only, and optional even there: work with no measurable total
    // leaves it off and gets an indeterminate bar rather than a false zero
    pct?: number;
    returnCode?: number;
    ts: number;
}

export interface AsyncOpEntry {
    id: string;
    label: string;
    cmd: string;
    stdout: string;
    stderr: string;
    returnCode: number | null;
    done: boolean;
    ts: number;
    lastTs: number;
    // Progress replaces rather than accumulates, so it needs its own fields:
    // appending it to stdout would grow the card without bound and drag the
    // output pane's scroll down on every tick
    pct: number | null;
    progressText: string;
    // Set once the operation has reported progress at all, which is what
    // decides whether the card shows a bar
    active: boolean;
}

export class StatusContextType {
    netBusyCount: number;
    setShowNetBusy: Function;
    setToasts: Function;
    toasts: object[];
    ts: number;
    asyncOps: AsyncOpEntry[];
    setAsyncOps: Function;
    setUnseenAsyncCount: Function;
    /*
     * Which operations have been seen, so a first event can be told from an
     * update. A plain set rather than state, because it is read in the same
     * breath as the event arrives and must never be a render old.
     */
    knownAsyncIds: Set<string>;
    // Assigned by App, which owns the panel these drive
    onAsyncOpNew: (id: string) => void;
    onAsyncOpEnd: (id: string, ok: boolean) => void;

    constructor() {
        // The real init is in App.js in a useEffect
        this.netBusyCount = 0;
        this.toasts = [];
        this.setToasts = () => {};
        this.ts = 0;
        this.setShowNetBusy = () => {};
        this.asyncOps = [];
        this.setAsyncOps = () => {};
        this.setUnseenAsyncCount = () => {};
        this.knownAsyncIds = new Set();
        this.onAsyncOpNew = () => {};
        this.onAsyncOpEnd = () => {};
    }

    /*
     * Every event folds into the operation's card through one functional
     * update. It has to be functional: this.asyncOps is a snapshot handed over
     * once per render, so reading it here made each event in a tick start from
     * the same stale array and overwrite the one before it. A progress stream
     * loses events that way, and postInfo below never showed its own text,
     * since its second and third calls could not see the entry its first had
     * just created.
     */
    receiveAsyncOp(event: AsyncOpEvent) {
        const isNew = !this.knownAsyncIds.has(event.id);
        if (isNew) {
            this.knownAsyncIds.add(event.id);
            if (this.knownAsyncIds.size > MAX_LOG_ENTRIES * 2) {
                // Sets iterate in insertion order, so the newest survive
                this.knownAsyncIds = new Set([ ...this.knownAsyncIds ].slice(-MAX_LOG_ENTRIES));
            }
        }
        this.setAsyncOps((prev: AsyncOpEntry[]) => {
            const idx = prev.findIndex(e => e.id === event.id);
            if (idx === -1) {
                const entry: AsyncOpEntry = {
                    id: event.id,
                    label: event.label,
                    cmd: event.stream === 'cmd' ? event.text : '',
                    stdout: event.stream === 'stdout' ? event.text : '',
                    stderr: event.stream === 'stderr' ? event.text : '',
                    returnCode: event.stream === 'result' ? (event.returnCode ?? -1) : null,
                    done: event.stream === 'result',
                    ts: event.ts,
                    lastTs: event.ts,
                    pct: event.stream === 'progress' ? (event.pct ?? null) : null,
                    progressText: event.stream === 'progress' ? event.text : '',
                    active: event.stream === 'progress',
                };
                const added = [ ...prev, entry ];
                if (added.length > MAX_LOG_ENTRIES) {
                    return added.slice(added.length - MAX_LOG_ENTRIES);
                }
                return added;
            }
            const cur = prev[idx];
            const updated: AsyncOpEntry = { ...cur, lastTs: event.ts };
            if (event.stream === 'cmd') {
                updated.cmd = event.text;
            } else if (event.stream === 'stdout') {
                updated.stdout = cur.stdout + event.text;
            } else if (event.stream === 'stderr') {
                updated.stderr = cur.stderr + event.text;
            } else if (event.stream === 'progress') {
                updated.pct = event.pct ?? null;
                updated.progressText = event.text;
                updated.active = true;
            } else if (event.stream === 'result') {
                updated.returnCode = event.returnCode ?? -1;
                updated.done = true;
                if (updated.pct !== null) {
                    updated.pct = 100;
                }
                if (event.text) {
                    updated.progressText = event.text;
                }
            }
            const ops = [ ...prev ];
            ops[idx] = updated;
            return ops;
        });
        /*
         * Told after the fold, and outside the updater, which has to stay pure.
         * Both go through here rather than through the socket handler alone, so
         * a locally raised operation like postInfo drives the panel the same
         * way a server side one does.
         */
        if (isNew) {
            this.onAsyncOpNew(event.id);
        }
        if (event.stream === 'result') {
            this.onAsyncOpEnd(event.id, (event.returnCode ?? -1) === 0);
        }
    }

    postError(msg: string) {
        let toasts = [...this.toasts];
        toasts.push({ id: Date.now() + Math.random(), msg, ts: Date.now(), type: 'error', acked: false });
        if (toasts.length > MAX_LOG_ENTRIES) {
            toasts = toasts.slice(toasts.length - MAX_LOG_ENTRIES);
        }
        this.setToasts(toasts);
    };
    postInfo(msg: string) {
        const id = `info-${Date.now()}-${Math.random()}`;
        const ts = Date.now();
        this.receiveAsyncOp({ id, label: '[info]', stream: 'cmd', text: '', ts });
        this.receiveAsyncOp({ id, label: '[info]', stream: 'stdout', text: msg, ts });
        this.receiveAsyncOp({ id, label: '[info]', stream: 'result', text: '', returnCode: 0, ts });
    };
    removeMsg(idx: number) { 
        let toasts = [...this.toasts];
        toasts.splice(idx, 1); 
        this.setToasts(toasts);
    };
    removeMsgById(id: number) {
        const toasts = this.toasts.filter((msg: any) => msg.id !== id);
        this.setToasts(toasts);
    };
    ackMsgById(id: number) {
        const toasts = this.toasts.map((msg: any) => {
            if (msg.id === id && msg.type === 'error') {
                return { ...msg, acked: true };
            }
            return msg;
        });
        this.setToasts(toasts);
    };
    netBusy() { 
        this.netBusyCount++; 
        if (this.netBusyCount > 0) {
            this.setShowNetBusy(true);
        }
        this.ts = Date.now();
    };
    netIdle() { 
        this.netBusyCount--;
        if (this.netBusyCount <= 0) {
            if (Date.now() - this.ts < blinkTime) {
                setTimeout(() => {
                    if (this.netBusyCount <= 0) {
                        this.setShowNetBusy(false);
                    }
                }, blinkTime);
            } else {
                this.setShowNetBusy(false);
            }
            this.netBusyCount = 0;
        }
    }
};

let ctx = new StatusContextType();

export const StatusContext = createContext<StatusContextType>(ctx);